// frontend/src/services/ausencias.js
import http from "./http";

/* ================= Helpers ================= */

function aliasEmail(obj) {
  const o = obj || {};
  return (
    o.usuario_email ??
    o.user_email ??
    o.email ??
    o?.usuario?.email ??
    o?.user?.email ??
    o?.requested_by_email ??
    null
  );
}

function normEstado(value) {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return null;
  if (["PENDIENTE", "PENDING"].includes(v)) return "PENDIENTE";
  if (["APROBADA", "APPROVED"].includes(v)) return "APROBADA";
  if (["RECHAZADA", "REJECTED"].includes(v)) return "RECHAZADA";
  if (["EXPIRADA", "EXPIRED"].includes(v)) return "EXPIRADA";
  return v;
}

async function buildAusenciasMap(token, ids) {
  if (!ids || ids.length === 0) return new Map();
  const todas = await http.get(token, `/ausencias`);
  const map = new Map();
  (todas || []).forEach((a) => map.set(a.id, a));
  return map;
}

/**
 * Hidrata solicitudes de cambio/cancelación con la AUSENCIA base,
 * y deja el payload INTACTO para que el componente pueda leer la propuesta.
 *
 * Importante: NO mezclamos fechas/horas con payload aquí.
 * - Original → viene de r.* o de la base.
 * - Propuesta → el componente la lee de `payload`.
 */
function hydrateRequest(kind, rows, baseMap) {
  return (rows || []).map((r) => {
    const base = r.ausencia || baseMap.get(r.ausencia_id) || {};
    const email = r.usuario_email ?? aliasEmail(base) ?? aliasEmail(r) ?? null;

    // ORIGINAL (nunca metemos aquí payload)
    const fecha_inicio = r.fecha_inicio ?? base.fecha_inicio ?? null;
    const fecha_fin = r.fecha_fin ?? base.fecha_fin ?? null;
    const hora_inicio = r.hora_inicio ?? base.hora_inicio ?? null;
    const hora_fin = r.hora_fin ?? base.hora_fin ?? null;

    const estado = normEstado(
      r.estado ?? r.status ?? r.request_status ?? null
    );

    const created_at =
      r.created_at ?? r.createdAt ?? base.created_at ?? null;

    return {
      id: r.id,
      usuario_email: email,

      // Tipo de la fila de solicitud (pill CAMBIO/CANCELACION)
      tipo: kind === "change" ? "CAMBIO" : "CANCELACION",

      // ORIGINAL (para mostrar "Original:")
      fecha_inicio,
      fecha_fin,
      hora_inicio,
      hora_fin,

      parcial: (r.parcial ?? base.parcial) ?? false,
      retribuida: (r.retribuida ?? base.retribuida) ?? false,

      // Para pintar “Permisos · Cita médica”
      subtipo: r.subtipo ?? base.subtipo ?? null,
      tipo_base: r.tipo_base ?? base.tipo ?? null,

      estado,
      created_at,

      motivo: r.motivo ?? base.motivo ?? null,

      // Claves para el panel
      _is_request: true,
      _request_kind: kind,
      _base_ausencia_id: r.ausencia_id,

      // MUY IMPORTANTE: payload crudo (para leer la propuesta Nueva)
      payload: r.payload ?? null,

      // También exponemos la ausencia original por si el componente quiere usarla directamente
      ausencia: base,

      // raw por si hace falta depurar
      _raw: r,
    };
  });
}

/* ================= API pública ================= */

/**
 * Saldo (widget / admin)
 * - Empleado: getBalance(token) → usa usuario del token (comportamiento intacto)
 * - Admin: getBalance(token, { email, userId, anio|year }) → calcula para ese usuario/año
 */
async function getBalance(token, opts = {}) {
  try {
    const q = [];
    if (opts.userId) q.push(`user_id=${encodeURIComponent(opts.userId)}`);
    if (opts.email) q.push(`email=${encodeURIComponent(opts.email)}`);
    const anio = opts.anio ?? opts.year;
    if (anio != null) q.push(`year=${encodeURIComponent(anio)}`);

    const path = `/ausencias/balance${q.length ? `?${q.join("&")}` : ""}`;
    return await http.get(token, path);
  } catch (e) {
    console.error("Error al obtener saldo de vacaciones:", e);
    return {
      anio: opts.anio ?? opts.year ?? new Date().getFullYear(),
      saldos: [],
    };
  }
}

// Ausencias base
async function listarTodas(token) {
  const rows = await http.get(token, `/ausencias`);
  return (rows || []).map((a) => ({
    ...a,
    usuario_email: a.usuario_email ?? aliasEmail(a),
    estado: a.estado ? normEstado(a.estado) : normEstado(a),
  }));
}

/**
 * Para la vista Empleado → "Mis solicitudes".
 * Filtra en cliente por el email del usuario.
 */
async function listarMiasPorEmail(token, email) {
  const todas = await listarTodas(token);
  const target = String(email || "").trim().toLowerCase();
  return (todas || []).filter(
    (a) =>
      String(a.usuario_email || aliasEmail(a) || "")
        .trim()
        .toLowerCase() === target
  );
}

/**
 * Feed unificado (ausencias + solicitudes cambio/cancelación)
 * GET /ausencias/feed?desde=YYYY-MM-DD&hasta=YYYY-MM-DD&estado=...&usuario=email
 */
async function listarFeed(token, opts = {}) {
  const q = [];
  if (opts.desde) q.push(`desde=${encodeURIComponent(opts.desde)}`);
  if (opts.hasta) q.push(`hasta=${encodeURIComponent(opts.hasta)}`);
  if (opts.estado) q.push(`estado=${encodeURIComponent(opts.estado)}`);
  if (opts.usuario) q.push(`usuario=${encodeURIComponent(opts.usuario)}`);
  const path = `/ausencias/feed${q.length ? `?${q.join("&")}` : ""}`;

  const rows = await http.get(token, path);
  return (rows || []).map((x) => ({
    ...x,
    estado: normEstado(x.estado),
  }));
}

// === Crear / Validar / Reglas (para "Solicitar") ============================

// GET /ausencias/reglas  (user_id|email|year opcionales)
async function reglas(token, opts = {}) {
  const q = [];
  if (opts.userId) q.push(`user_id=${encodeURIComponent(opts.userId)}`);
  if (opts.email) q.push(`email=${encodeURIComponent(opts.email)}`);
  if (opts.year) q.push(`year=${encodeURIComponent(opts.year)}`);
  const path = `/ausencias/reglas${q.length ? `?${q.join("&")}` : ""}`;
  return await http.get(token, path);
}

// POST /ausencias/validar
// body: { usuario_id?, usuario_email?, tipo, desde, hasta, medio_dia, ... }
async function validar(token, body) {
  return await http.post(token, `/ausencias/validar`, body);
}

// POST /ausencias   (alias /ausencias/crear)
async function crear(token, body) {
  // usa el endpoint principal; si prefieres el alias:
  // return await http.post(token, `/ausencias/crear`, body);
  return await http.post(token, `/ausencias`, body);
}

// === Admin: aprobar/rechazar ================================================

async function aprobar(token, id) {
  return await http.post(token, `/ausencias/${id}/aprobar`, {});
}

async function rechazar(token, id, motivo) {
  return await http.post(token, `/ausencias/${id}/rechazar`, { motivo });
}

// === Solicitudes CAMBIO/CANCELACIÓN =========================================

async function listarCambios(token) {
  const rows = await http.get(token, `/ausencias/change-requests`);
  const ids = (rows || []).map((r) => r.ausencia_id).filter(Boolean);
  const baseMap = await buildAusenciasMap(token, ids);
  return hydrateRequest("change", rows, baseMap);
}

async function listarCancelaciones(token) {
  const rows = await http.get(token, `/ausencias/cancel-requests`);
  const ids = (rows || []).map((r) => r.ausencia_id).filter(Boolean);
  const baseMap = await buildAusenciasMap(token, ids);
  return hydrateRequest("cancel", rows, baseMap);
}

async function aprobarCambio(token, id) {
  return await http.post(token, `/ausencias/change-requests/${id}/approve`, {});
}

async function rechazarCambio(token, id, motivo) {
  return await http.post(token, `/ausencias/change-requests/${id}/reject`, {
    motivo,
  });
}

async function aprobarCancelacion(token, id, comentario = "") {
  return await http.post(token, `/ausencias/cancel-requests/${id}/approve`, {
    comentario,
  });
}

async function rechazarCancelacion(token, id, comentario = "") {
  return await http.post(token, `/ausencias/cancel-requests/${id}/reject`, {
    comentario,
  });
}

// En el flujo de empleado "editar futura", tu UI crea una solicitud:
async function solicitarCambio(token, ausenciaId, payload, motivo = null) {
  return await http.post(token, `/ausencias/${ausenciaId}/change-requests`, {
    payload,
    motivo,
  });
}

async function solicitarCancelacion(token, ausenciaId, motivo) {
  return await http.post(token, `/ausencias/${ausenciaId}/cancel-requests`, {
    motivo,
  });
}

// === Movimientos de saldo (admin) ===========================================

/** POST /ausencias/movimientos  { usuario_email|usuario_id, year?, tipo, fecha, delta, motivo, referencia? } */
async function crearMovimiento(token, body) {
  return await http.post(token, `/ausencias/movimientos`, body);
}

/* ======== Exports ======== */

export const ausenciasService = {
  // saldo
  getBalance,
  // base
  listarTodas,
  listarMiasPorEmail,
  // feed unificado
  listarFeed,
  // reglas / validar / crear
  reglas,
  validar,
  crear,
  // admin aprobar/rechazar
  aprobar,
  rechazar,
  // solicitudes (listados)
  listarCambios,
  listarCancelaciones,
  // solicitudes (acciones)
  solicitarCambio,
  solicitarCancelacion,
  aprobarCambio,
  rechazarCambio,
  aprobarCancelacion,
  rechazarCancelacion,
  // movimientos
  crearMovimiento,
};

export default ausenciasService;

// exports nombrados (compat)
export {
  getBalance,
  listarTodas,
  listarMiasPorEmail,
  listarFeed,
  reglas,
  validar,
  crear,
  aprobar,
  rechazar,
  listarCambios,
  listarCancelaciones,
  solicitarCambio,
  solicitarCancelacion,
  aprobarCambio,
  rechazarCambio,
  aprobarCancelacion,
  rechazarCancelacion,
  crearMovimiento,
};